import { GET, POST } from '@/shared/api/requests';
import type {
  PhotoGalleryFilters,
  PhotoGalleryPageParams,
  PhotoGroupsResponse,
  PhotoSourceType,
  SignedUploadResponse,
  GalleryPhoto,
} from '../types';

const BASE = '/photos/gallery';

export const getPhotoGroups = async (
  projectId: number,
  filters: PhotoGalleryFilters = {},
  pageParams: PhotoGalleryPageParams = {},
): Promise<PhotoGroupsResponse> => {
  const params = new URLSearchParams({
    project_id: String(projectId),
    group_by: 'date',
  });
  if (filters.search) params.set('search', filters.search);
  if (filters.source_type) params.set('source_type', filters.source_type);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (pageParams.limit) params.set('limit', String(pageParams.limit));
  if (pageParams.offset) params.set('offset', String(pageParams.offset));

  const response = await GET(`${BASE}/?${params.toString()}`);
  return response.data as PhotoGroupsResponse;
};

export const requestPhotoUploadUrl = async (payload: {
  project_id: number;
  file_name: string;
  content_type: string;
  source_type?: PhotoSourceType;
  source_object_id?: string;
}): Promise<SignedUploadResponse> => {
  const response = await POST(`${BASE}/upload-url/`, payload);
  return response.data as SignedUploadResponse;
};

export const finalizeUploadedPhoto = async (payload: {
  project_id: number;
  photo_uuid: string;
  gcs_object_path: string;
  original_file_name: string;
  mime_type?: string;
  file_size?: number;
  source_type?: PhotoSourceType;
  source_object_id?: string;
  captured_at?: string | null;
}): Promise<GalleryPhoto> => {
  const response = await POST(`${BASE}/finalize/`, payload);
  return response.data as GalleryPhoto;
};

export const uploadFileToSignedUrl = async (
  file: File,
  signedUrl: string,
  onProgress?: (progressPct: number) => void,
): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (!onProgress || !event.lengthComputable) return;
      onProgress((event.loaded / event.total) * 100);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};
